import { useRef, useState } from "react";
import {
  IdcardOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  UserOutlined,
} from "./icons";
import {
  Avatar,
  Button,
  DescriptionList,
  Dialog,
  EmptyState,
  Notice,
  Spinner,
} from "./controls";
import "./UserProfileDrawer.css";

export interface UserProfile {
  id: string;
  name: string;
  displayName?: string;
  avatarUrl?: string;
  email?: string;
  phone?: string;
  title?: string;
  employeeNo?: string;
  bio?: string;
}

export interface UserProfileDrawerProps {
  loading?: boolean;
  meId?: string;
  onClose: () => void;
  onStartChat?: (userId: string) => void | Promise<void>;
  open: boolean;
  profile: UserProfile | null;
}

export function UserProfileDrawer({
  loading = false,
  meId,
  onClose,
  onStartChat,
  open,
  profile,
}: UserProfileDrawerProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="用户详情"
      width={400}
      className="ark-user-profile-drawer"
    >
      <ProfileContent
        key={profile?.id ?? "empty"}
        loading={loading}
        meId={meId}
        profile={profile}
        onStartChat={onStartChat}
      />
    </Dialog>
  );
}

function ProfileContent({
  loading,
  meId,
  profile,
  onStartChat,
}: {
  loading: boolean;
  meId: string | undefined;
  profile: UserProfile | null;
  onStartChat: UserProfileDrawerProps["onStartChat"];
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const displayName = profile?.displayName || profile?.name || "未知用户";
  const profileID = profile?.id;
  const canStartChat =
    Boolean(profileID) && profileID !== meId && Boolean(onStartChat);
  async function startChat() {
    if (
      !profileID ||
      !onStartChat ||
      !canStartChat ||
      loading ||
      inFlight.current
    )
      return;
    inFlight.current = true;
    setPending(true);
    setError("");
    try {
      await onStartChat(profileID);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "无法发起私聊，请重试");
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }
  if (loading)
    return (
      <div className="ark-user-profile-loading" aria-busy="true">
        <Spinner tip="正在加载用户资料" />
      </div>
    );
  if (!profile) return <EmptyState description="无法加载用户资料" />;
  return (
    <>
      <header className="ark-user-profile-header">
        <Avatar
          aria-label={`${displayName}的头像`}
          className="ark-user-profile-avatar"
          key={profile.avatarUrl}
          role="img"
          size={88}
          src={profile.avatarUrl}
        >
          {displayName.trim().slice(0, 1) || <UserOutlined />}
        </Avatar>
        <h3>{displayName}</h3>
        {profile.title ? <p>{profile.title}</p> : null}
        {canStartChat ? (
          <Button
            icon={<MessageOutlined />}
            type="primary"
            loading={pending}
            onClick={() => void startChat()}
          >
            发起私聊
          </Button>
        ) : null}
      </header>
      {error ? <Notice type="error" title={error} /> : null}
      <DescriptionList
        className="ark-user-profile-fields"
        grouped
        items={[
          {
            key: "name",
            term: (
              <>
                <UserOutlined />
                用户名
              </>
            ),
            detail: profile.name,
          },
          ...(profile.email
            ? [
                {
                  key: "email",
                  term: (
                    <>
                      <MailOutlined />
                      邮箱
                    </>
                  ),
                  detail: profile.email,
                },
              ]
            : []),
          ...(profile.phone
            ? [
                {
                  key: "phone",
                  term: (
                    <>
                      <PhoneOutlined />
                      手机
                    </>
                  ),
                  detail: profile.phone,
                },
              ]
            : []),
          ...(profile.employeeNo
            ? [
                {
                  key: "employee",
                  term: (
                    <>
                      <IdcardOutlined />
                      工号
                    </>
                  ),
                  detail: profile.employeeNo,
                },
              ]
            : []),
        ]}
      />
      <section className="ark-user-profile-bio" aria-label="个性签名">
        <h4>个性签名</h4>
        <p>{profile.bio || "这个人还没有填写个性签名"}</p>
      </section>
    </>
  );
}
